import { RequestService } from 'Utils/Api';
import { profileSDK } from 'Utils/ProfileSDK';
import { logger } from 'Utils/Logger';
import { capitalizeFirstLetter } from 'Utils/Format';
import { getTokenExpirations, getUser } from 'Utils/UserCookie';
import { getDefaultConfig } from 'Utils/Store';

export const updateEmail = async (profileId, payload) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('update email');
  try {
    const { data } = await RequestService.send({
      url: `${foxApi}/update/${profileId}/v3`,
      method: 'PUT',
      authenticate: true,
      data: payload,
      customHeaders: { 'x-api-key': accountApiKey },
    });
    logger.apiResponse('update email');
    return data;
  } catch (error) {
    logger.apiError('update email', null, error);
    console.error(error.message);
    return false;
  }
};

//API calls from Profile SDK

export const updateUser = async (user) => {
  //omitting payload from log because personal information should not be sent 3rd parties
  logger.apiRequest('update user');
  try {
    const response = await profileSDK().update(user);
    if (response) {
      //omitting response from log because personal information should not be sent 3rd parties
      logger.apiResponse('update user');
      return response;
    }
    throw new Error('update user empty response');
  } catch (error) {
    logger.apiError('update user', null, error);
    return error;
  }
};

export const checkExistingUser = async (email) => {
  //omitting payload from log because personal information should not be sent 3rd parties
  logger.apiRequest('check existing user');
  try {
    const response = profileSDK().status({ email });
    logger.apiResponse('check existing user', response);
    return response;
  } catch (error) {
    console.error(error.message);
    logger.apiError('check existing user', null, error);
    throw error;
  }
};

export const resetEmail = async (email) => {
  let res;
  //omitting payload from log because personal information should not be sent 3rd parties
  logger.apiRequest('reset email');
  try {
    res = await profileSDK().resetPassword({ email });
    logger.apiResponse('reset email', res);
    res = res && res.message && res.message === 'Reset Email Sent' ? true : res.message;
  } catch (error) {
    const errorMessage = error.errorMessage
      ? `${error.statusCode}: ${capitalizeFirstLetter(error.errorMessage)}`
      : error.message;
    logger.apiError('reset email', null, error);
    res = errorMessage;
  }
  return res;
};

export const registerSecondScreenCode = (regCode) => {
  logger.apiRequest('register second screen code', regCode);
  return profileSDK()
    .userLoggedInOrRegistered({ regCode, isRegister: true })
    .then((res) => {
      logger.apiResponse('register second screen code', res);
      return res;
    })
    .catch((error) => {
      console.error(error.message);
      logger.apiError('register second screen code', null, error);
      return { error: error.message };
    });
};

export const fetchMobileRegcode = (deviceId) => {
  logger.apiRequest('fetch mobile regcode', deviceId);
  return profileSDK()
    .regCodeDeeplink({ deviceId })
    .then((res) => {
      logger.apiResponse('fetch mobile regcode', res);
      return res;
    })
    .catch((error) => {
      console.error(error.message);
      logger.apiError('fetch mobile regcode', null, error);
      return { error: error.message };
    });
};

export const login = ({ email, password, recaptchaToken, deviceId, mvpd, mvpdHash }) => {
  //omitting payload from log because personal information should not be sent 3rd parties
  logger.apiRequest('login');
  return profileSDK()
    .login({ email, password, recaptchaToken, deviceId, mvpd, mvpdHash })
    .then((res) => {
      /*TODO: CLEAN UP */
      //omitting response from log because personal information should not be sent 3rd parties
      logger.apiResponse('fetch mobile regcode', res);
      const expirations = getTokenExpirations(res.tokenExpiration);
      return { ...res, ...expirations };
    })
    .catch((error) => {
      console.error(error.message);
      logger.apiError('login', null, error);
      throw error;
    });
};

export const logout = async () => {
  try {
    const user = getUser();
    let { refreshToken } = user;
    logger.apiRequest('logout');
    const result = await profileSDK().logout({ refreshTokenHint: refreshToken });
    logger.apiResponse('logout', result);
    return result;
  } catch (error) {
    logger.apiError('logout', null, error);
    return { error: true };
  }
};

export const checkRefreshToken = ({ accessTokenExpiration, refreshToken }) => {
  logger.apiRequest('check refresh token');
  return profileSDK()
    .checkRefreshToken({ tokenExpiration: accessTokenExpiration, refreshToken })
    .then((res) => {
      logger.apiResponse('check refresh token', res);
      /*TODO: CLEAN UP */
      const expirations = getTokenExpirations(res.tokenExpiration);
      return { ...res, ...expirations };
    })
    .catch((error) => {
      console.error(error.message);
      logger.apiError('check refresh token', null, error);
      return { error: error.message };
    });
};

export const createPaymentAccount = async (user, recaptchaToken) => {
  const { firstName, lastName, password = '', email } = user;
  const payload = {
    CreateUserReqMessage: {
      firstName,
      lastName,
      customerUsername: email,
      customerPassword: password,
      email,
      recaptchaToken,
      isGenerateToken: false,
      dmaId: '001',
      verifyEmail: false,
      address: {
        country: 'US',
      },
    },
  };
  //omitting payload from log because personal information should not be sent 3rd parties
  logger.apiRequest('create payment account');
  return profileSDK()
    .createPaymentAccount(payload)
    .then((res) => {
      //omitting response from log because personal information should not be sent 3rd parties
      logger.apiResponse('create payment account');
      return res.CreateUserResponseMessage;
    })
    .catch((error) => {
      console.error(error);
      logger.apiError('create payment account', null, error);
      return error;
    });
};

export const updatePaymentAccount = async ({ profileId, firstName, lastName }) => {
  const payload = {
    UpdateProfileRequestMessage: {
      firstName,
      lastName,
      emailNotificationFlag: true,
    },
  };
  //omitting payload from log because personal information should not be sent 3rd parties
  logger.apiRequest('update payment account');
  return profileSDK()
    .updatePaymentAccount(profileId, payload)
    .then((res) => {
      //omitting response from log because personal information should not be sent 3rd parties
      logger.apiResponse('update payment account');
      return { updateSuccess: !!res.UpdateProfileResponseMessage };
    })
    .catch((error) => {
      console.error(error.message);
      logger.apiError('update payment account', null, error);
      return { error: error.message };
    });
};

export const getXid = async () => {
  try {
    //disabling log as this function gets called a lot and would generate a ton of expensive logs.
    //logger.apiRequest('getXid');

    const appEnv = getDefaultConfig('appEnv');

    let baseUrl = `https://qa-nation.foxnews.com`;
    if (appEnv === 'production') {
      baseUrl = `https://nation.foxnews.com`;
    } else if (appEnv === 'staging') {
      baseUrl = `https://stage-nation.foxnews.com`;
    }

    /* profileSDK().getXid() may not always make a network request - may also get xid from xid cookie
      profile sdk dev: "Xid should be parsed from the Cookie, but there are some tricks with it.
      In certain cases, the cookie is accessible with JavaScript, influenced by factors like DNS,
      3rd-party domains security, etc. If you observe this happening, it might be the reason.
      Try running your implementation in a browser and check 'document.cookie' in the browser console.
      If you get an empty response but can still see the cookie in developer tools, that's expected."
    */
    const result = await profileSDK().getXid({ baseUrl });
    //disabling log as this function gets called a lot and would generate a ton of expensive logs.
    // logger.apiResponse('getXid', result);
    return result;
  } catch (error) {
    logger.apiError('getXid', null, error);
    return { error: true };
  }
};

export const submitXidEvent = async (xid = '') => {
  try {
    // disabling log as this function gets called a lot and would generate a ton of expensive logs.
    // logger.apiRequest('submitXidEvent', payload);
    // default eventType in SDK is 'view' so not passing it for now since that is only event type we are using
    const result = await profileSDK().submitXidEvent({ xid, originBu: 'foxnation' });
    //disabling log as this function gets called a lot and would generate a ton of expensive logs.
    // logger.apiResponse('submitXidEvent', result);
    return result;
  } catch (error) {
    logger.apiError('submitXidEvent', null, error);
    return { error: true };
  }
};
